import { db } from "../services/firebase";
import { emitFirestoreError } from "../lib/errorBus";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  increment,
  deleteDoc,
  runTransaction,
} from "firebase/firestore";

// Helpers
function keyId(parts) {
  return parts.map(s => String(s || "").toLowerCase().trim().replace(/\s+/g, "_")).join("__");
}

// ===== Uniforms =====
function normalizeCampus(campus) {
  const x = String(campus || "").toLowerCase().trim();
  if (x.includes("gapan")) return "gapan";
  if (x.includes("bulacan")) return "bulacan";
  return x;
}
export async function uniforms_getInventory(campus) {
  const q = query(collection(db, "uniforms_inventory"), where("campus", "==", normalizeCampus(campus)));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => r.archived !== true);
}

export async function uniforms_getBeginningInventory(campus, year) {
  const base = collection(db, "uniforms_beginning_inventory");
  const y = Number(year);
  const q = query(
    base,
    where("campus", "==", normalizeCampus(campus)),
    where("year", "==", y)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function uniforms_upsertBeginningInventory(campus, year, item) {
  const id = keyId([item.product, item.size, campus, year]);
  const ref = doc(db, "uniforms_beginning_inventory", id);
  const payload = {
    campus: normalizeCampus(campus),
    year: Number(year),
    product: item.product,
    size: item.size,
    sku: item.sku || "",
    beginningQty: Number(item.beginningQty || 0),
    note: item.note || ""
  };
  try {
    await setDoc(ref, payload, { merge: true });
  } catch (err) {
    emitFirestoreError({
      code: err?.code,
      message: err?.message,
      operation: "Uniforms: Save beginning inventory",
      retry: () => setDoc(ref, payload, { merge: true })
    });
    throw err;
  }
  return id;
}

export async function uniforms_upsertItem(campus, item) {
  const id = keyId([item.product, item.size, campus]);
  const ref = doc(db, "uniforms_inventory", id);
  try {
    await setDoc(ref, {
      product: item.product,
      size: item.size,
      sku: item.sku || "",
      barcode: item.barcode || "",
      price: Number(item.price || 0),
      cost: Number(item.cost || 0),
      stock: Number(item.stock || 0),
      campus: normalizeCampus(campus),
      archived: item.archived === true ? true : false
    }, { merge: true });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Uniforms: Save item", retry: () => setDoc(ref, {
      product: item.product,
      size: item.size,
      sku: item.sku || "",
      barcode: item.barcode || "",
      price: Number(item.price || 0),
      cost: Number(item.cost || 0),
      stock: Number(item.stock || 0),
      campus: normalizeCampus(campus),
      archived: item.archived === true ? true : false
    }, { merge: true }) });
    throw err;
  }
  return id;
}

export async function uniforms_adjustStock(campus, product, size, delta) {
  const id = keyId([product, size, campus]);
  const ref = doc(db, "uniforms_inventory", id);
  try {
    await setDoc(ref, { product, size, campus: normalizeCampus(campus) }, { merge: true });
    await updateDoc(ref, { stock: increment(Number(delta || 0)) });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Uniforms: Adjust stock", retry: () => updateDoc(ref, { stock: increment(Number(delta || 0)) }) });
    throw err;
  }
}

export async function uniforms_deleteItem(campus, product, size) {
  const id = keyId([product, size, campus]);
  const ref = doc(db, "uniforms_inventory", id);
  try {
    await deleteDoc(ref);
  } catch (err) {
    // If deletion not permitted for this role, fall back to soft-archive
    try {
      await updateDoc(ref, { archived: true });
    } catch (e2) {
      emitFirestoreError({ code: err?.code || e2?.code, message: err?.message || e2?.message, operation: "Uniforms: Archive item", retry: () => updateDoc(ref, { archived: true }) });
      // Do not rethrow to avoid breaking UI for uniforms role
    }
  }
}

export async function uniforms_recordSale(campus, items, options = {}) {
  const total = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.price || 0), 0);
  const saleDate = options.date || new Date().toISOString().slice(0, 10);
  try {
    await addDoc(collection(db, "uniforms_sales"), {
      campus: normalizeCampus(campus),
      date: saleDate,
      items,
      total
    });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Uniforms: Record sale", retry: () => addDoc(collection(db, "uniforms_sales"), {
      campus: normalizeCampus(campus),
      date: saleDate,
      items,
      total
    }) });
    throw err;
  }
  // decrement stocks
  for (const it of items) {
    await uniforms_adjustStock(campus, it.product, it.size, -Number(it.qty || 0));
  }
}

export async function uniforms_getSales(campus) {
  const q = query(collection(db, "uniforms_sales"), where("campus", "==", normalizeCampus(campus)));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Deliveries (receiving)
export async function uniforms_recordDelivery(campus, { supplier, drNo, date, items = [] }) {
  const totalCost = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.cost || 0), 0);
  try {
    await addDoc(collection(db, "uniforms_receipts"), {
      campus: normalizeCampus(campus),
      supplier: supplier || "",
      drNo: drNo || "",
      date: date || new Date().toISOString().slice(0, 10),
      items,
      totalCost
    });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Uniforms: Record delivery", retry: () => addDoc(collection(db, "uniforms_receipts"), {
      campus: normalizeCampus(campus),
      supplier: supplier || "",
      drNo: drNo || "",
      date: date || new Date().toISOString().slice(0, 10),
      items,
      totalCost
    }) });
    throw err;
  }
  // increment stocks (do not overwrite on-hand) and update metadata only when provided
  for (const it of items) {
    const qty = Number(it.qty || 0);
    if (!qty) continue;
    const id = keyId([it.product, it.size, campus]);
    const ref = doc(db, "uniforms_inventory", id);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const meta = {
        campus: normalizeCampus(campus),
        product: it.product,
        size: it.size,
        ...(it.sku ? { sku: String(it.sku) } : {}),
        ...(it.barcode ? { barcode: String(it.barcode) } : {}),
        ...(Number(it.price) > 0 ? { price: Number(it.price) } : {}),
        ...(Number(it.cost) > 0 ? { cost: Number(it.cost) } : {}),
        archived: false
      };
      if (!snap.exists()) {
        tx.set(ref, { ...meta, stock: qty }, { merge: true });
      } else {
        tx.set(ref, meta, { merge: true });
        tx.update(ref, { stock: increment(qty) });
      }
    });
  }
}

export async function uniforms_setOnHand(campus, product, size, stock) {
  const id = keyId([product, size, campus]);
  const ref = doc(db, "uniforms_inventory", id);
  try {
    await setDoc(ref, { campus: normalizeCampus(campus), product, size, stock: Number(stock || 0) }, { merge: true });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Uniforms: Set on-hand", retry: () => setDoc(ref, { campus: normalizeCampus(campus), product, size, stock: Number(stock || 0) }, { merge: true }) });
    throw err;
  }
}

export async function uniforms_getDeliveries(campus) {
  const q = query(collection(db, "uniforms_receipts"), where("campus", "==", normalizeCampus(campus)));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Purchase Orders / Reorder Requests
export async function uniforms_createOrder(campus, { supplier = "", items = [], notes = "" }) {
  try {
    await addDoc(collection(db, "uniforms_orders"), {
      campus: normalizeCampus(campus),
      date: new Date().toISOString().slice(0, 10),
      supplier,
      items,
      notes,
      status: "open"
    });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Uniforms: Create order", retry: () => addDoc(collection(db, "uniforms_orders"), {
      campus: normalizeCampus(campus),
      date: new Date().toISOString().slice(0, 10),
      supplier,
      items,
      notes,
      status: "open"
    }) });
    throw err;
  }
}

export async function uniforms_getOrders(campus) {
  const q = query(collection(db, "uniforms_orders"), where("campus", "==", normalizeCampus(campus)));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ===== Books =====
export async function books_getInventory(campus, schoolYear) {
  const base = collection(db, "books_inventory");
  let q;
  if (!schoolYear || schoolYear === "all") {
    q = query(base, where("campus", "==", normalizeCampus(campus)));
  } else {
    q = query(base, where("campus", "==", normalizeCampus(campus)), where("schoolYear", "==", schoolYear));
  }
  let snap = await getDocs(q);
  let rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (rows.length === 0) {
    // Fallback for legacy docs missing campus or schoolYear fields
    const allSnap = await getDocs(base);
    const targetC = normalizeCampus(campus);
    rows = allSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(doc => {
        const c = normalizeCampus(doc.campus);
        const syOk = !schoolYear || schoolYear === "all" || String(doc.schoolYear) === String(schoolYear);
        // Treat missing campus as belonging to selected campus to avoid hiding legacy records
        return syOk && (c ? c === targetC : true);
      });
  }
  return rows;
}

export async function books_upsertBook(campus, schoolYear, book) {
  const id = keyId([book.title, book.isbn, book.grade, campus, schoolYear]);
  const ref = doc(db, "books_inventory", id);
  try {
    await setDoc(ref, {
      title: book.title,
      isbn: book.isbn || "",
      publisher: book.publisher || "",
      grade: book.grade || "",
      cost: Number(book.cost || 0),
      price: Number(book.price || 0),
      received: Number(book.received || 0),
      sold: Number(book.sold || 0),
      campus: normalizeCampus(campus),
      schoolYear
    }, { merge: true });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Books: Save inventory", retry: () => setDoc(ref, {
      title: book.title,
      isbn: book.isbn || "",
      publisher: book.publisher || "",
      grade: book.grade || "",
      cost: Number(book.cost || 0),
      price: Number(book.price || 0),
      received: Number(book.received || 0),
      sold: Number(book.sold || 0),
      campus: normalizeCampus(campus),
      schoolYear
    }, { merge: true }) });
    throw err;
  }
  return id;
}

export async function books_adjustReceived(campus, schoolYear, bookId, delta) {
  const ref = doc(db, "books_inventory", bookId);
  try {
    await updateDoc(ref, { received: increment(Number(delta || 0)) });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Books: Adjust received", retry: () => updateDoc(ref, { received: increment(Number(delta || 0)) }) });
    throw err;
  }
}

export async function books_saveBundle(campus, schoolYear, grade, items, price) {
  const id = keyId([campus, schoolYear, grade]);
  const ref = doc(db, "books_bundles", id);
  try {
    await setDoc(ref, {
      campus: normalizeCampus(campus),
      schoolYear,
      grade,
      items,
      price: Number(price || 0)
    }, { merge: true });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Books: Save bundle", retry: () => setDoc(ref, {
      campus: String(campus).toLowerCase(),
      schoolYear,
      grade,
      items,
      price: Number(price || 0)
    }, { merge: true }) });
    throw err;
  }
  return id;
}

export async function books_getBundle(campus, schoolYear, grade) {
  const q = query(
    collection(db, "books_bundles"),
    where("campus", "==", normalizeCampus(campus)),
    where("schoolYear", "==", schoolYear),
    where("grade", "==", grade)
  );
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return docs[0] || null;
}

export async function books_recordSale(campus, schoolYear, date, grade, qty, bundle, extras = {}) {
  try {
    await addDoc(collection(db, "books_sales"), {
      campus: normalizeCampus(campus),
      schoolYear,
      date,
      grade,
      qty: Number(qty || 0),
      price: Number(bundle.price || 0),
      total: Number(qty || 0) * Number(bundle.price || 0),
      items: bundle.items,
      ...extras
    });
  } catch (err) {
    emitFirestoreError({ code: err?.code, message: err?.message, operation: "Books: Record sale", retry: () => addDoc(collection(db, "books_sales"), {
      campus: normalizeCampus(campus),
      schoolYear,
      date,
      grade,
      qty: Number(qty || 0),
      price: Number(bundle.price || 0),
      total: Number(qty || 0) * Number(bundle.price || 0),
      items: bundle.items,
      ...extras
    }) });
    throw err;
  }
  // increment sold count for each book
  for (const it of bundle.items) {
    const invId = it.id ? String(it.id) : keyId([it.title, it.isbn || "", it.grade || "", campus, schoolYear]);
    const ref = doc(db, "books_inventory", invId);
    try {
      await setDoc(ref, { sold: Number(it.qty || 0) }, { merge: true });
      const inc = Number(it.qty || qty || 1);
      await updateDoc(ref, { sold: increment(inc) });
    } catch (err) {
      emitFirestoreError({ code: err?.code, message: err?.message, operation: "Books: Update sold", retry: () => updateDoc(ref, { sold: increment(Number(qty || 0)) }) });
      throw err;
    }
  }
}

export async function books_getSales(campus, schoolYear) {
  const base = collection(db, "books_sales");
  let q;
  if (!schoolYear || schoolYear === "all") {
    q = query(base, where("campus", "==", normalizeCampus(campus)));
  } else {
    q = query(base, where("campus", "==", normalizeCampus(campus)), where("schoolYear", "==", schoolYear));
  }
  let snap = await getDocs(q);
  let rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (rows.length === 0) {
    // Fallback for legacy docs with missing campus or schoolYear
    const allSnap = await getDocs(base);
    const targetC = normalizeCampus(campus);
    rows = allSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(doc => {
        const c = normalizeCampus(doc.campus);
        const syOk = !schoolYear || schoolYear === "all" || String(doc.schoolYear) === String(schoolYear);
        return syOk && (c ? c === targetC : true);
      });
  }
  return rows;
}
